/*
 * PSYKHE PLP behavior overrides for the Olivela Shopify theme.
 *
 * This file customizes PSYKHE web components to match the production
 * PLP on www.olivela.com. Customization happens through public render
 * callbacks, facet config, typed `psykhe-*` events, and theme-owned
 * layout glue.
 *
 * Production reference: https://www.olivela.com/collections/new-arrivals
 *
 * File layout:
 *   1.  Sticky header offset       — getStickyOffset() dynamic header height
 *   2.  Collection metadata        — GLOBAL_COLLECTION_MAPPING helpers
 *   3.  Public override helpers    — renderCard, renderChip, facet config
 *   4.  Badge & tag data helpers   — getOlivelaCuratedBadge, getOlivelaCardTag
 *   5.  Pagination polish          — hide pagination when single page
 *   6.  Sticky controls bar        — sentinel + IntersectionObserver
 *   7.  Event/runtime glue         — typed analytics + content tiles
 *   8.  Controls bar text tweaks   — FILTER/SORT labels, Products count
 *   9. Search suggestion data      — fallback chips + helpers
 *   10. Bootstrap                  — DOMContentLoaded + public event wiring
 *   11. Pagination skeleton overlay — shimmer on slow page changes
 */

// ---------------------------------------------------------------------------
// 1. STICKY HEADER OFFSET
//
// getStickyOffset() returns the pixel offset below which a sticky element
// should sit (i.e. the bottom of Olivela's fixed site header). Reads a CSS
// variable first, then falls back to measuring the actual header element,
// then to hardcoded 119/70 values.
//
// Measures the live header so the offset survives announcement-bar
// dismissal and responsive breakpoint changes.
// ---------------------------------------------------------------------------

function parsePxValue(value) {
  if (!value) return 0;
  const parsed = Number.parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeOlivelaStickyOffset(offset) {
  if (!Number.isFinite(offset) || offset <= 0) {
    return 0;
  }

  if (window.innerWidth > 768 && offset >= 119) {
    return offset - 1;
  }

  return offset;
}

var _olivelaHeaderEl = null;

function findOlivelaHeaderElement() {
  if (_olivelaHeaderEl instanceof HTMLElement && _olivelaHeaderEl.isConnected) {
    return _olivelaHeaderEl;
  }
  // Olivela uses a <sticky-header> custom element inside the .shopify-section-header
  // wrapper. Fall back to the section wrapper if the custom element isn't present.
  var candidates = [
    "sticky-header",
    ".shopify-section-header sticky-header",
    ".shopify-section-header .header-wrapper",
    ".shopify-section-header header",
    ".shopify-section-header",
    "header.header",
    ".header__wrapper",
  ];
  for (var i = 0; i < candidates.length; i++) {
    var el = document.querySelector(candidates[i]);
    if (el instanceof HTMLElement && el.offsetHeight > 0) {
      _olivelaHeaderEl = el;
      return el;
    }
  }
  return null;
}

// Measures the live bottom edge of the Olivela header on every call rather
// than caching a value, because the header height changes when the announcement
// bar is dismissed and during the sticky-show transition. Math.floor avoids
// the 1px subpixel gap that appears between the header and the PLP sticky bar
// when the browser rounds a fractional `bottom` value upward.
function getStickyOffset() {
  var styles = window.getComputedStyle(document.documentElement);
  var explicitSticky = parsePxValue(
    styles.getPropertyValue("--sticky-header-offset")
  );
  if (explicitSticky > 0) return normalizeOlivelaStickyOffset(explicitSticky);

  var header = findOlivelaHeaderElement();
  if (header) {
    var rect = header.getBoundingClientRect();
    if (rect.height > 0) {
      return normalizeOlivelaStickyOffset(Math.floor(rect.bottom));
    }
  }

  return window.innerWidth <= 768 ? 70 : 118;
}

// ---------------------------------------------------------------------------
// 2. COLLECTION METADATA
//
// Olivela theme sets window.GLOBAL_COLLECTION_MAPPING (e.g. "Clothing > Tops")
// to indicate the current PLP's category depth. We use this to decide:
//   - which category levels to show/hide in the filter panel drill-down
// Reference: Olivela theme's GLOBAL_COLLECTION_MAPPING liquid variable.
// ---------------------------------------------------------------------------

function getOlivelaCollectionMapping() {
  if (typeof window.GLOBAL_COLLECTION_MAPPING !== "string") {
    return "";
  }

  return window.GLOBAL_COLLECTION_MAPPING.trim();
}

function getOlivelaCollectionSegments() {
  const mapping = getOlivelaCollectionMapping();
  if (!mapping) {
    return [];
  }

  return mapping
    .split(">")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

// ---------------------------------------------------------------------------
// 3. PUBLIC OVERRIDE HELPERS
// ---------------------------------------------------------------------------

var olivelaRuntimeState = {
  instance: "default",
  page: 1,
  total: 0,
  itemsOnPage: 0,
  facets: [],
  categoryTitleLabels: {
    category_2: [],
  },
  runtimeInit: false,
};

var olivelaAutoOpenState = {
  didBootstrapCategoryOpen: false,
  prevCat0Selected: [],
  prevCat1Selected: [],
  prevCat2Selected: [],
  // Set on psykhe-refinements-change and flushed on psykhe-loading-change
  // (loading:false), so the child accordion opens only after fresh facet
  // data has rendered.
  pendingDrillOpenKey: null,
};

// ---------------------------------------------------------------------------
// 4. BADGE & TAG DATA HELPERS + CONSTANTS
//
// Shared data extraction helpers for Olivela's product-card override.
// Reads named_tags.Badge_curated1 / Badge_curated for the top-left badge and
// named_tags.Tag_curated1 / Tag_curated for the text below the price.
// ---------------------------------------------------------------------------

// Class fallback for browsers without :has() support (see psykhe.css layout rule).
function initOlivelaAppBlock(root) {
  if (!(root instanceof HTMLElement)) return;
  const appBlock = root.closest(".shopify-app-block");
  if (appBlock instanceof HTMLElement && !appBlock.classList.contains("psykhe-app-block")) {
    appBlock.classList.add("psykhe-app-block");
  }
}

// Badge priority promotes "Selling Fast" when present in the array.
function normalizeOlivelaBadgeValue(value, preferSellingFast) {
  if (Array.isArray(value)) {
    const firstString = value.find(
      (entry) => typeof entry === "string" && entry.trim().length > 0
    );
    if (
      preferSellingFast &&
      value.some(
        (entry) =>
          typeof entry === "string" &&
          entry.trim().toLowerCase() === "selling fast"
      )
    ) {
      return "Selling Fast";
    }
    return typeof firstString === "string" ? firstString.trim() : "";
  }

  if (typeof value !== "string") return "";

  const normalized = value.trim();
  if (!normalized) return "";
  if (preferSellingFast && normalized.toLowerCase() === "selling fast") {
    return "Selling Fast";
  }
  return normalized;
}

function getOlivelaNamedTags(item) {
  if (!item || typeof item !== "object") {
    return null;
  }

  if (item.named_tags && typeof item.named_tags === "object") {
    return item.named_tags;
  }

  if (item.metadata && typeof item.metadata === "object") {
    var metadataNamedTags = item.metadata.named_tags;
    if (metadataNamedTags && typeof metadataNamedTags === "object") {
      return metadataNamedTags;
    }
  }

  if (item.namedTags && typeof item.namedTags === "object") {
    return item.namedTags;
  }

  return null;
}

// Reads named_tags.Badge_curated1 then Badge_curated. Typical Olivela
// values: "New Arrival", "Selling Fast".
function getOlivelaCuratedBadge(item) {
  if (!item || typeof item !== "object") return "";

  const namedTags = getOlivelaNamedTags(item);
  if (!namedTags || typeof namedTags !== "object") return "";

  const primary = normalizeOlivelaBadgeValue(namedTags.Badge_curated1, true);
  if (primary) return primary;

  return normalizeOlivelaBadgeValue(namedTags.Badge_curated, false);
}

// Returns the card tag for an item using the following priority order:
//   1. Tag2 = "ENGRAVING AVAILABLE" (always shown, highest priority)
//   2. Tag2 (other values) — suppressed for carousels, Badge=New, Availability=Pre-order, Tag_curated=MADE TO ORDER
//   3. Tag1
//   4. Tag (only when value is "fast shipping")
//   5. Tag_curated1
//   6. Tag_curated
function getOlivelaCardTag(item) {
  if (!item || typeof item !== "object") return "";

  var namedTags = getOlivelaNamedTags(item);
  if (!namedTags || typeof namedTags !== "object") return "";

  function firstVal(/** @type {string|string[]|undefined} */ v) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (Array.isArray(v)) {
      var found = v.find(function(x) { return typeof x === "string" && x.trim(); });
      return found ? found.trim() : "";
    }
    return "";
  }

  var tag2 = firstVal(namedTags.Tag2);
  if (tag2 === "ENGRAVING AVAILABLE") return tag2;

  if (tag2) {
    var isCarousel = !!item.isCarousel;
    var badgeNew = firstVal(namedTags.Badge) === "New";
    var preOrder = firstVal(namedTags.Availability) === "Pre-order";
    var madeToOrder = firstVal(namedTags.Tag_curated) === "MADE TO ORDER";
    if (!isCarousel && !badgeNew && !preOrder && !madeToOrder) return tag2;
  }

  var tag1 = firstVal(namedTags.Tag1);
  if (tag1) return tag1;

  var tag = firstVal(namedTags.Tag);
  if (tag && tag.toLowerCase() === "fast shipping") return tag;

  var tagCurated1 = firstVal(namedTags.Tag_curated1);
  if (tagCurated1) return tagCurated1;

  return firstVal(namedTags.Tag_curated);
}

function formatOlivelaCardTagLabel(value) {
  var normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return "";
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getOlivelaProductPrice(product) {
  if (typeof product?.price === "string") {
    return String(product.price).trim();
  }
  return formatOlivelaMoneyAmount(product?.price?.current?.amount);
}

function getOlivelaCompareAtPrice(product) {
  return formatOlivelaMoneyAmount(product?.price?.compareAt?.amount);
}

function toOlivelaImageObject(value) {
  if (!value) return null;
  if (typeof value === "string") return { url: value };
  if (typeof value.url === "string") return value;
  if (typeof value.src === "string") {
    return { url: value.src, width: value.width, height: value.height, altText: value.alt };
  }
  return null;
}

function getOlivelaProductPrimaryImage(product) {
  if (Array.isArray(product?.images) && product.images.length > 0) {
    var obj = toOlivelaImageObject(product.images[0]);
    if (obj) return obj;
  }
  return toOlivelaImageObject(product?.image || product?.featured_image);
}

function getOlivelaProductHoverImage(product) {
  if (Array.isArray(product?.images) && product.images.length > 1) {
    var obj = toOlivelaImageObject(product.images[1]);
    if (obj) return obj;
  }
  return toOlivelaImageObject(product?.imageHover);
}

function formatOlivelaMoneyAmount(amount) {
  if (typeof amount !== "number" || !Number.isFinite(amount)) {
    return "";
  }
  return "$" + amount.toLocaleString("en-US");
}

function getOlivelaProductUrl(product) {
  if (product && typeof product.url === "string" && product.url.trim()) {
    return product.url.trim();
  }

  var handle =
    typeof product?.handle === "string" && product.handle.trim()
      ? product.handle.trim()
      : "";

  return handle ? "/products/" + handle : "#";
}

function renderOlivelaProductCard(product, ctx, onClick, options) {
  var primaryImage = getOlivelaProductPrimaryImage(product);
  var hoverImage = getOlivelaProductHoverImage(product);
  var title = product?.title || "";
  var vendor = product?.vendor || "";
  var priceFormatted = getOlivelaProductPrice(product);
  var compareAt = getOlivelaCompareAtPrice(product);
  var onSale = !!(product?.price && product.price.onSale);
  var url = getOlivelaProductUrl(product);
  var badge = getOlivelaCuratedBadge(product);
  var cardTag = formatOlivelaCardTagLabel(getOlivelaCardTag(product));
  var linkTabIndex = options && typeof options.linkTabIndex === "number"
    ? options.linkTabIndex
    : null;
  var handle = typeof product?.handle === "string" ? product.handle.trim() : "";
  var rawPrice = product?.price?.current?.amount != null
    ? product.price.current.amount
    : (typeof product?.price === "string" ? product.price.replace(/[^0-9.]/g, "") : "");
  var imageUrl = primaryImage?.url || "";

  return ctx.html`
    <a
      part="card card-link"
      href=${url}
      aria-label=${title || vendor || "View product"}
      tabindex=${linkTabIndex != null ? String(linkTabIndex) : ctx.nothing}
      @click=${onClick || ctx.nothing}
    >
      <div part="media">
        ${badge ? ctx.html`<span part="badge">${badge}</span>` : ""}
        ${primaryImage?.url
          ? ctx.html`
              <img
                part="image image-primary"
                src=${primaryImage.url}
                alt=${primaryImage.altText || title || ""}
                loading="lazy"
                width=${primaryImage.width || 620}
                height=${primaryImage.height || 775}
              />
            `
          : ""}
        ${hoverImage?.url
          ? ctx.html`
              <img
                part="image image-hover"
                src=${hoverImage.url}
                alt=""
                aria-hidden="true"
                loading="lazy"
                width=${hoverImage.width || primaryImage?.width || 620}
                height=${hoverImage.height || primaryImage?.height || 775}
              />
            `
          : ""}
      </div>
      ${handle
        ? ctx.html`<div
            part="wishlist-anchor"
            data-handle=${handle}
            data-name=${title}
            data-image=${imageUrl}
            data-price=${String(rawPrice)}
            data-url=${url}
          ></div>`
        : ""}
      <div part="content content-link">
        <p part="vendor">${vendor}</p>
        <p part="title">${title}</p>
        <div part="pricing">
          ${onSale && compareAt
            ? ctx.html`<span part="compare-price">${compareAt}</span>`
            : ""}
          <span part="price">${priceFormatted}</span>
        </div>
        ${cardTag ? ctx.html`<span part="more-info">${cardTag}</span>` : ""}
      </div>
    </a>
  `;
}

function formatOlivelaPriceChipLabel(label) {
  var raw = String(label == null ? "" : label).trim();
  if (!raw) return raw;

  var numbers = raw.replace(/\$/g, "").match(/\d+(?:\.\d+)?/g);
  if (numbers && numbers.length >= 2) {
    return "$" + numbers[0] + " - $" + numbers[1];
  }
  if (numbers && numbers.length === 1) {
    var amount = "$" + numbers[0];
    if (/\bunder\b|up to|^</i.test(raw)) return amount + " and under";
    if (/\+|from/i.test(raw)) return amount + " +";
    return amount;
  }

  return raw.charAt(0) === "$" ? raw : "$" + raw;
}

function renderOlivelaFilterChip(chip, ctx) {
  var label =
    typeof chip.label === "string" && chip.label.includes(">")
      ? normalizeOlivelaHierarchicalLabel(chip.label)
      : chip.label;
  if (String(chip.facetLabel || "").toLowerCase() === "price") {
    label = formatOlivelaPriceChipLabel(label);
  }

  return ctx.html`
    <button
      ${ctx.a11y.chipRemove(chip)}
      part="chip"
      @click=${ctx.actions.remove(chip)}
    >
      <span part="remove">
        <span part="chip-remove-icon" aria-hidden="true"></span>
      </span>
      <span part="chip-label">
        <span part="chip-facet-label">${chip.facetLabel}:</span>
        <span part="chip-value-label">${label}</span>
      </span>
    </button>
  `;
}

function getSelectedFacetLabels(facets, facetKey) {
  var facet = getFacetModelByKey(facets, facetKey);
  if (!facet || !Array.isArray(facet.options)) {
    return [];
  }

  return facet.options
    .filter(function (option) {
      return option && option.selected === true;
    })
    .map(function (option) {
      return String(option.label || "").trim();
    })
    .filter(function (label) {
      return label.length > 0;
    });
}

var OLIVELA_PRIORITY_FILTERS_BY_COLLECTION = {
  Jewelry: ["Category", "Material", "Size", "Color", "Designer", "Price"],
  Clothing: ["Category", "Size", "Color", "Occasion", "Price"],
  Shoes: ["Category", "Size", "Color", "Occasion", "Price"],
  "Bags-accessories": ["Category", "Size", "Color", "Occasion", "Price"],
  "New-arrivals": ["Category", "Designer", "Size", "Color", "Material", "Price"],
  Search: ["Category", "Designer", "Size", "Color", "Material", "Price"],
};

var OLIVELA_PRIORITY_FILTER_FACET_KEYS = {
  Category: "category_0",
  Designer: "designer",
  Size: "us_size",
  Color: "color",
  Material: "material",
  Occasion: "occasion",
  Price: "price",
};

function getFacetModelByKey(facets, facetKey) {
  return Array.isArray(facets)
    ? facets.find(function (entry) {
        return entry && entry.key === facetKey;
      }) || null
    : null;
}

function isRenderableOlivelaFacetOption(option) {
  if (!option || typeof option !== "object") {
    return false;
  }

  return (
    option.selected === true ||
    option.disabled !== true ||
    (typeof option.count === "number" && option.count > 0)
  );
}

function isRenderableOlivelaFacet(facets, facetKey) {
  var facet = getFacetModelByKey(facets, facetKey);
  if (!facet || typeof facet !== "object") {
    return false;
  }

  if (typeof facet.selectedCount === "number" && facet.selectedCount > 0) {
    return true;
  }

  if (isOlivelaPriceFacet(facet)) {
    // Price is renderable when numeric bounds are available.
    return (
      typeof facet.currentMin === "number" &&
      typeof facet.currentMax === "number" &&
      facet.currentMax >= facet.currentMin
    );
  }

  if (!Array.isArray(facet.options)) {
    return false;
  }

  return facet.options.some(isRenderableOlivelaFacetOption);
}

function isOlivelaPriceFacet(facet) {
  if (!facet || facet.type !== "range") return false;
  return facet.key === "price" || facet.label === "Price";
}

function getOlivelaPriorityFilterCollectionKey() {
  var mapping = getOlivelaCollectionMapping();
  if (!mapping) {
    var path = String(window.location.pathname || "").toLowerCase();
    if (path.includes("/search")) return "Search";
    if (path.includes("/new-arrivals")) return "New-arrivals";
    return "";
  }

  var normalized = mapping.replace(/ & /g, "-").replace(/\s+/g, "-");
  return (
    Object.keys(OLIVELA_PRIORITY_FILTERS_BY_COLLECTION).find(function (key) {
      return normalized.includes(key);
    }) || ""
  );
}

function getOlivelaPriorityFilterLabels() {
  var collectionKey = getOlivelaPriorityFilterCollectionKey();
  return collectionKey
    ? OLIVELA_PRIORITY_FILTERS_BY_COLLECTION[collectionKey] || []
    : [];
}

function resolveOlivelaPriorityFacetKey(label) {
  if (label === "Designer") {
    if (isRenderableOlivelaFacet(olivelaRuntimeState.facets, "designer")) {
      return "designer";
    }
    if (isRenderableOlivelaFacet(olivelaRuntimeState.facets, "vendor")) {
      return "vendor";
    }
    if (isRenderableOlivelaFacet(olivelaRuntimeState.facets, "brand")) {
      return "brand";
    }
  }

  if (label === "Size") {
    if (isRenderableOlivelaFacet(olivelaRuntimeState.facets, "us_size")) {
      return "us_size";
    }
    if (isRenderableOlivelaFacet(olivelaRuntimeState.facets, "size")) {
      return "size";
    }
  }

  if (label !== "Category") {
    return OLIVELA_PRIORITY_FILTER_FACET_KEYS[label] || "";
  }

  var collectionKey = getOlivelaPriorityFilterCollectionKey();
  var mappingDepth = getOlivelaCollectionSegments().length;
  if (collectionKey !== "New-arrivals" && collectionKey !== "Search") {
    if (mappingDepth === 1) return "category_1";
    if (mappingDepth >= 2) return "category_2";
  }

  return "category_0";
}

function getOlivelaPriorityFacetLabel(facetKey) {
  var facet = getFacetModelByKey(olivelaRuntimeState.facets, facetKey);
  if (facet && typeof facet.label === "string" && facet.label.trim()) {
    return facet.label.trim();
  }

  switch (facetKey) {
    case "category_0":
    case "category_1":
    case "category_2":
      return "Category";
    case "designer":
    case "vendor":
    case "brand":
      return "Designer";
    case "us_size":
      return "Size";
    case "color":
      return "Color";
    case "material":
      return "Material";
    case "occasion":
      return "Occasion";
    default:
      return "";
  }
}

/**
 * Fuse.js adapter for the designer facet search input.
 *
 * Plugs into `FacetConfig.searchFilter` on the <psykhe-filter-panel>. The
 * panel calls this once per keystroke (only while the query is non-empty)
 * with the current unselected values. Selected designers are pinned by the
 * panel and never passed through here, so this matcher cannot hide them.
 *
 * Fuse config: `threshold: 0.4`, `ignoreLocation: true`,
 * `minMatchCharLength: 0`.
 *
 * The Fuse instance is memoized on the values-array identity so we avoid
 * rebuilding the index on every keystroke — the panel passes the same array
 * until facet data changes. Falls back to case-insensitive substring if
 * `window.Fuse` is not loaded (CDN blocked, load ordering, etc.).
 */
function createOlivelaDesignerSearchFilter() {
  var cachedFuse = null;
  var cachedValues = null;

  return function designerSearchFilter(query, values) {
    if (window.Fuse) {
      if (cachedValues !== values) {
        cachedValues = values;
        cachedFuse = new window.Fuse(values, {
          keys: ["label"],
          threshold: 0.4,
          ignoreLocation: true,
          minMatchCharLength: 0,
        });
      }
      return cachedFuse.search(query).map(function (result) {
        return result.item;
      });
    }

    var needle = String(query).toLowerCase();
    return values.filter(function (value) {
      var label = value && value.label ? String(value.label).toLowerCase() : "";
      return label.indexOf(needle) !== -1;
    });
  };
}

var olivelaDesignerSearchFilter = createOlivelaDesignerSearchFilter();

function buildOlivelaFilterFacetConfig() {
  var collectionSegments = getOlivelaCollectionSegments();
  var mappingDepth = collectionSegments.length;
  var sourceFacets = olivelaRuntimeState.facets;
  var cat0Selected = getSelectedFacetLabels(sourceFacets, "category_0");
  var cat1Selected = getSelectedFacetLabels(sourceFacets, "category_1");
  var cat2Selected = getSelectedFacetLabels(sourceFacets, "category_2");

  if (cat1Selected.length > 0 && cat2Selected.length === 0) {
    olivelaRuntimeState.categoryTitleLabels.category_2 = cat1Selected.slice();
  } else if (cat1Selected.length === 0 && cat2Selected.length === 0) {
    olivelaRuntimeState.categoryTitleLabels.category_2 = [];
  } else if (
    cat1Selected.length > 0 &&
    olivelaRuntimeState.categoryTitleLabels.category_2.length === 0
  ) {
    olivelaRuntimeState.categoryTitleLabels.category_2 = cat1Selected.slice();
  }

  var effectiveCat0Selected =
    cat0Selected.length > 0
      ? cat0Selected
      : mappingDepth > 0
      ? [collectionSegments[0]]
      : [];
  var effectiveCat1Selected =
    cat2Selected.length > 0 &&
    olivelaRuntimeState.categoryTitleLabels.category_2.length > 0
      ? olivelaRuntimeState.categoryTitleLabels.category_2
      : cat1Selected.length > 0
      ? cat1Selected
      : mappingDepth > 1
      ? [collectionSegments.slice(0, 2).join(" > ")]
      : [];

  var showCategoryOne =
    mappingDepth < 2 &&
    (mappingDepth === 1 || effectiveCat0Selected.length > 0) &&
    isRenderableOlivelaFacet(sourceFacets, "category_1");
  var showCategoryTwo =
    (mappingDepth >= 2 ||
      (effectiveCat1Selected.length > 0 && showCategoryOne)) &&
    isRenderableOlivelaFacet(sourceFacets, "category_2");

  if (!Array.isArray(sourceFacets)) {
    return [{ key: "designer", searchable: true, visible: false }];
  }

  var mapped = sourceFacets
    .filter(function (f) {
      return f && typeof f.key === "string" && f.key.length > 0;
    })
    .map(function (f) {
      var key = f.key;
      switch (key) {
        case "category_0":
          return {
            key: "category_0",
            visible:
              mappingDepth === 0 &&
              isRenderableOlivelaFacet(sourceFacets, "category_0"),
          };
        case "category_1":
          return {
            key: "category_1",
            visible: showCategoryOne,
            label:
              showCategoryOne && effectiveCat0Selected.length > 0
                ? effectiveCat0Selected.join(", ")
                : undefined,
            labelFormatter: normalizeOlivelaHierarchicalLabel,
          };
        case "category_2":
          return {
            key: "category_2",
            visible: showCategoryTwo,
            label:
              showCategoryTwo && effectiveCat1Selected.length > 0
                ? effectiveCat1Selected
                    .map(normalizeOlivelaHierarchicalLabel)
                    .join(", ")
                : undefined,
            labelFormatter: normalizeOlivelaHierarchicalLabel,
          };
        case "designer":
          return {
            key: "designer",
            searchable: true,
            searchFilter: olivelaDesignerSearchFilter,
            visible: isRenderableOlivelaFacet(sourceFacets, "designer"),
          };
        case "color":
          return {
            key: "color",
            swatchMap: olivelaColorMap,
            visible: isRenderableOlivelaFacet(sourceFacets, "color"),
          };
        default:
          return {
            key: key,
            visible: isRenderableOlivelaFacet(sourceFacets, key),
          };
      }
    });

  var hasDesigner = mapped.some(function (f) { return f.key === "designer"; });
  if (!hasDesigner) {
    mapped = [{ key: "designer", searchable: true, visible: false }].concat(mapped);
  }
  return mapped;
}

var OLIVELA_PRICE_BUCKETS = [200, 300, 500, 1000];

var olivelaRangePanelId = 0;
var olivelaRangePanelIds = new WeakMap();

function olivelaGetRangeControlKey(facet, panel) {
  var panelId = panel ? olivelaRangePanelIds.get(panel) : null;
  if (panel && !panelId) {
    olivelaRangePanelId += 1;
    panelId = "olivela-price-range-" + olivelaRangePanelId;
    olivelaRangePanelIds.set(panel, panelId);
  }
  return (panelId || "olivela-price-range") + ":" + facet.key;
}

function olivelaMakeRangeBounds(min, max) {
  if (typeof min === "number" && typeof max === "number" && max > min) {
    return { min: min, max: max };
  }
  return null;
}

function olivelaMakePriceAvailabilityBounds(min, max) {
  if (typeof min === "number" && typeof max === "number" && max >= min) {
    return { min: min, max: max };
  }
  return null;
}

function buildOlivelaPriceBuckets(statsMin, statsMax, activeSelection) {
  var buckets = [];
  OLIVELA_PRICE_BUCKETS.forEach(function (threshold) {
    var bucket = {
      kind: "under",
      label: "$" + threshold + " and under",
      min: null,
      max: threshold,
    };
    var isActive = olivelaIsSamePriceSelection(activeSelection, bucket);
    if (isActive || (threshold >= statsMin && threshold <= statsMax)) {
      buckets.push({
        kind: bucket.kind,
        label: bucket.label,
        min: bucket.min,
        max: bucket.max,
      });
    }
  });
  var top = OLIVELA_PRICE_BUCKETS[OLIVELA_PRICE_BUCKETS.length - 1];
  var topBucket = {
    kind: "over",
    label: "$" + top + " +",
    min: top,
    max: null,
  };
  if (olivelaIsSamePriceSelection(activeSelection, topBucket) || statsMax >= top) {
    buckets.push(topBucket);
  }
  return buckets;
}

function olivelaGetMatchingPriceBucketIdx(selection, buckets) {
  if (!selection) return null;
  for (var i = 0; i < buckets.length; i++) {
    var bucket = buckets[i];
    if (olivelaIsSamePriceSelection(selection, bucket)) {
      return i;
    }
  }
  return null;
}

function olivelaIsSamePriceSelection(selection, bucket) {
  return (
    selection &&
    bucket &&
    selection.min === bucket.min &&
    selection.max === bucket.max
  );
}

function olivelaNormalizeSelectedRange(selection) {
  if (!selection || typeof selection !== "object" || Array.isArray(selection)) {
    return null;
  }

  var min =
    typeof selection.min === "number" && Number.isFinite(selection.min)
      ? selection.min
      : null;
  var max =
    typeof selection.max === "number" && Number.isFinite(selection.max)
      ? selection.max
      : null;

  if (min === null && max === null) return null;
  return { min: min, max: max, bucketIdx: null };
}

function olivelaParsePriceInput(value) {
  var trimmed = String(value == null ? "" : value).trim();
  if (trimmed === "") return null;
  var num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

function olivelaTogglePartToken(el, token, active) {
  if (!el) return;
  var parts = String(el.getAttribute("part") || "")
    .split(/\s+/)
    .filter(Boolean);
  var hasToken = parts.indexOf(token) !== -1;
  if (active && !hasToken) {
    parts.push(token);
  } else if (!active && hasToken) {
    parts = parts.filter(function (part) {
      return part !== token;
    });
  }
  el.setAttribute("part", parts.join(" "));
}

function olivelaSetPriceInputsInvalid(form, invalid) {
  if (!form) return;
  var errorId = form.getAttribute("data-olivela-error-id");
  var errorEl = form.querySelector("[data-olivela-error]");
  if (errorEl) {
    errorEl.textContent = invalid ? "Enter a valid price range." : "";
  }
  form.toggleAttribute("data-olivela-invalid", invalid);
  form
    .querySelectorAll('[data-olivela-bound]')
    .forEach(function (input) {
      input.setAttribute("aria-invalid", invalid ? "true" : "false");
      if (invalid && errorId) {
        input.setAttribute("aria-describedby", errorId);
      } else if (input.getAttribute("aria-describedby") === errorId) {
        input.removeAttribute("aria-describedby");
      }
      olivelaTogglePartToken(input, "range-input-invalid", invalid);
      olivelaTogglePartToken(
        input.closest('[part~="range-field"]'),
        "range-field-invalid",
        invalid
      );
    });
}

function olivelaSyncPriceRangeForm(form, min, max) {
  if (!form) return;
  var minEl = form.querySelector('[data-olivela-bound="min"]');
  var maxEl = form.querySelector('[data-olivela-bound="max"]');
  if (minEl) minEl.value = typeof min === "number" ? String(min) : "";
  if (maxEl) maxEl.value = typeof max === "number" ? String(max) : "";
  olivelaSetPriceInputsInvalid(form, false);
}

function olivelaRefreshPriceRangePanel(panel) {
  if (!panel) return;
  if (typeof panel.requestUpdate === "function") {
    panel.requestUpdate();
  }
}

function olivelaCommitPriceInputs(
  form,
  facetKey,
  actions,
  hasSelection,
  bounds
) {
  var minEl = form.querySelector('[data-olivela-bound="min"]');
  var maxEl = form.querySelector('[data-olivela-bound="max"]');
  var typedMin = olivelaParsePriceInput(minEl && minEl.value);
  var typedMax = olivelaParsePriceInput(maxEl && maxEl.value);
  olivelaSetPriceInputsInvalid(form, false);

  if (typedMin === null && typedMax === null) {
    if (minEl) minEl.value = "";
    if (maxEl) maxEl.value = "";
    if (hasSelection) {
      actions.setRange(facetKey, null, null);
      return true;
    }
    return false;
  }

  var nextMin = typedMin === null ? null : Math.max(0, typedMin);
  var nextMax = typedMax === null ? null : Math.max(0, typedMax);

  if (
    typeof nextMin === "number" &&
    typeof nextMax === "number" &&
    nextMin >= nextMax
  ) {
    olivelaSetPriceInputsInvalid(form, true);
    return false;
  }

  if (bounds) {
    var belowAvailable =
      typeof nextMax === "number" && nextMax < Math.floor(bounds.min);
    var aboveAvailable =
      typeof nextMin === "number" && nextMin > Math.ceil(bounds.max);
    if (belowAvailable || aboveAvailable) {
      olivelaSetPriceInputsInvalid(form, true);
      return false;
    }
  }

  if (minEl) minEl.value = typeof nextMin === "number" ? String(nextMin) : "";
  if (maxEl) maxEl.value = typeof nextMax === "number" ? String(nextMax) : "";

  actions.setRange(facetKey, nextMin, nextMax);
  return true;
}

function olivelaClearPresetSelection(form) {
  var presets = form.querySelectorAll('input[data-olivela-preset]');
  presets.forEach(function (preset) {
    preset.checked = false;
  });
}

function olivelaResetPriceRangeControls(scope) {
  var root = scope instanceof HTMLElement ? scope : document;
  root.querySelectorAll("psykhe-filter-panel").forEach(function (panel) {
    if (!panel.shadowRoot) return;
    panel.shadowRoot
      .querySelectorAll('[part~="range-controls"]')
      .forEach(function (form) {
        olivelaSyncPriceRangeForm(form, null, null);
        form
          .querySelectorAll('input[data-olivela-preset]')
          .forEach(function (preset) {
            preset.checked = false;
          });
      });
    if (typeof panel.requestUpdate === "function") {
      panel.requestUpdate();
    }
  });
}

function olivelaRenderPriceRange(facet, ctx, panel) {
  if (!isOlivelaPriceFacet(facet)) return null;

  var controlKey = olivelaGetRangeControlKey(facet, panel);
  var baselineBounds = olivelaMakeRangeBounds(
    facet.baselineMin,
    facet.baselineMax
  );
  if (!baselineBounds) return null;
  var availabilityBounds = olivelaMakePriceAvailabilityBounds(
    facet.currentMin,
    facet.currentMax
  );
  var inputBounds = baselineBounds;
  if (facet.selectedCount === 0 && availabilityBounds) {
    inputBounds =
      olivelaMakePriceAvailabilityBounds(
        Math.max(baselineBounds.min, availabilityBounds.min),
        Math.min(baselineBounds.max, availabilityBounds.max)
      ) || baselineBounds;
  }
  var selection = olivelaNormalizeSelectedRange(facet.selectedRange);
  // Without an active Price selection, show only presets available for the
  // current results. When Price is active, keep broader presets available so
  // the shopper can replace or clear the current price range.
  var presetBounds =
    facet.selectedCount > 0 ? baselineBounds : inputBounds;
  var statsMin = presetBounds.min;
  var statsMax = presetBounds.max;
  var buckets = buildOlivelaPriceBuckets(statsMin, statsMax, selection);

  if (selection && selection.bucketIdx == null) {
    var matchingBucketIdx = olivelaGetMatchingPriceBucketIdx(selection, buckets);
    if (matchingBucketIdx !== null) {
      selection = {
        min: selection.min,
        max: selection.max,
        bucketIdx: matchingBucketIdx,
      };
    }
  }
  var activeBucketIdx = selection ? selection.bucketIdx : null;

  function onInputType(event) {
    var form = event.currentTarget.closest('[part="range-controls"]');
    if (form) olivelaClearPresetSelection(form);
  }

  function onInputKey(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    var form = event.currentTarget.closest('[part="range-controls"]');
    if (form) {
      if (olivelaCommitPriceInputs(
        form,
        facet.key,
        ctx.actions,
        facet.selectedCount > 0,
        inputBounds
      )) {
        olivelaRefreshPriceRangePanel(panel);
      }
    }
  }

  function onInputBlur(event) {
    var form = event.currentTarget.closest('[part="range-controls"]');
    var nextFocus =
      event.relatedTarget && form && form.contains(event.relatedTarget)
        ? event.relatedTarget
        : null;
    if (
      nextFocus &&
      typeof nextFocus.hasAttribute === "function" &&
      nextFocus.hasAttribute("data-olivela-bound")
    ) {
      return;
    }
    if (form) {
      if (olivelaCommitPriceInputs(
        form,
        facet.key,
        ctx.actions,
        facet.selectedCount > 0,
        inputBounds
      )) {
        olivelaRefreshPriceRangePanel(panel);
      }
    }
  }

  function onPresetClick(idx, bucket, active) {
    return function (event) {
      var form = event.currentTarget.closest('[part="range-controls"]');
      var activeNow = selection
        ? olivelaIsSamePriceSelection(selection, bucket)
        : active;
      if (activeNow) {
        event.currentTarget.checked = false;
        olivelaSyncPriceRangeForm(form, null, null);
        ctx.actions.setRange(facet.key, null, null);
        olivelaRefreshPriceRangePanel(panel);
        return;
      }

      if (form) {
        var presets = form.querySelectorAll('input[data-olivela-preset]');
        presets.forEach(function (preset) {
          preset.checked = preset === event.currentTarget;
        });
      }
      olivelaSyncPriceRangeForm(form, bucket.min, bucket.max);
      ctx.actions.setRange(facet.key, bucket.min, bucket.max);
      olivelaRefreshPriceRangePanel(panel);
    };
  }

  var minDisplay =
    selection && typeof selection.min === "number" ? String(selection.min) : "";
  var maxDisplay =
    selection && typeof selection.max === "number" ? String(selection.max) : "";
  var presetName = controlKey.replace(/[^a-zA-Z0-9_-]/g, "-");
  var minInputName = presetName + "-min";
  var maxInputName = presetName + "-max";
  var errorId = presetName + "-error";

  return ctx.html`
    <div
      part="range-controls"
      role="group"
      aria-label=${facet.label}
      data-olivela-error-id=${errorId}
    >
      <label part="range-field">
        <span part="range-field-label">Min</span>
        <input
          part="range-input"
          data-olivela-bound="min"
          name=${minInputName}
          type="text"
          inputmode="decimal"
          placeholder=""
          autocomplete="off"
          aria-label="Minimum price"
          .value=${minDisplay}
          @input=${onInputType}
          @keydown=${onInputKey}
          @blur=${onInputBlur}
        />
      </label>
      <label part="range-field">
        <span part="range-field-label">Max</span>
        <input
          part="range-input"
          data-olivela-bound="max"
          name=${maxInputName}
          type="text"
          inputmode="decimal"
          placeholder=""
          autocomplete="off"
          aria-label="Maximum price"
          .value=${maxDisplay}
          @input=${onInputType}
          @keydown=${onInputKey}
          @blur=${onInputBlur}
        />
      </label>
      <span
        id=${errorId}
        part="range-error"
        data-olivela-error
        aria-live="polite"
        role="status"
      ></span>
      <div part="price-presets" role="group" aria-label=${facet.label + " presets"}>
        ${buckets.map(function (b, idx) {
          var active = activeBucketIdx === idx;
          return ctx.html`
            <label part=${active ? "price-preset-option option option-selected" : "price-preset-option option"}>
              <span part="price-preset-label option-label">
                <input
                  part="price-preset-input option-input"
                  data-olivela-preset
                  type="checkbox"
                  name=${presetName}
                  .checked=${active}
                  @click=${onPresetClick(idx, b, active)}
                />
                ${b.label}
              </span>
            </label>
          `;
        })}
      </div>
    </div>
  `;
}

function applyOlivelaFilterPanelConfig(panel) {
  if (!(panel instanceof HTMLElement)) {
    return;
  }

  panel.toggleAttribute("hide-count", true);
  if ("singleOpen" in panel) {
    panel.singleOpen = true;
  } else {
    panel.setAttribute("single-open", "");
  }
  if ("scrollOnExpand" in panel) {
    panel.scrollOnExpand = "start";
  } else {
    panel.setAttribute("scroll-on-expand", "start");
  }
  if ("hierarchicalMode" in panel) {
    panel.hierarchicalMode = "drill-down";
  } else {
    panel.setAttribute("hierarchical-mode", "drill-down");
  }
  var isDrawerPanel = !!panel.closest("psykhe-drawer");
  if (isDrawerPanel) {
    if ("facetSummary" in panel) {
      panel.facetSummary = "count";
    } else {
      panel.setAttribute("facet-summary", "count");
    }
  }
  if (isDrawerPanel && !panel.hasAttribute("max-visible-items")) {
    panel.setAttribute("max-visible-items", "0");
  }

  panel.facets = buildOlivelaFilterFacetConfig();
  panel.renderRange = function (facet, ctx) {
    return olivelaRenderPriceRange(facet, ctx, panel);
  };

  // Shadow DOM style injection: hide search clear icon & native clear button
  if (panel.shadowRoot && !panel.shadowRoot.querySelector('#olivela-facet-search-style')) {
    var shadowStyle = document.createElement('style');
    shadowStyle.id = 'olivela-facet-search-style';
    shadowStyle.textContent = '[part="facet-search-clear"] svg { display: none !important; } [part="facet-search-input"]::-webkit-search-cancel-button { -webkit-appearance: none; display: none !important; } [part="facet-search-input"]::-ms-clear { display: none !important; }';
    panel.shadowRoot.appendChild(shadowStyle);
  }
}

var WRAPPED_FETCHER_MARKER = Symbol("psykhe.olivelaWrapped");
var WRAPPED_CLEAR_FILTERS_MARKER = Symbol("psykhe.olivelaClearWrapped");

// Upserts the "VIEW ALL" link next to the "Most Popular Searches" label.
// `visible` is driven from the suggestions fetcher — the fetcher knows
// whether we have a query AND whether that query returned products, which
// is the ground truth for showing the link. Driving from DOM `input` events
// cannot distinguish "typed but no results" and would leak stale links
// across dialog open/close cycles.
function syncOlivelaViewAllLink(input, row, visible) {
  if (!row) return;
  var existing = row.querySelector(".psykhe-olivela-queries-heading__link");

  if (!visible) {
    if (existing) existing.remove();
    return;
  }

  var raw = typeof input.value === "string" ? input.value : "";
  var query = raw.trim();

  if (!query) {
    if (existing) existing.remove();
    return;
  }
  var href = "/search?q=" + encodeURIComponent(query);
  if (existing) {
    if (existing.getAttribute("href") !== href) existing.setAttribute("href", href);
    return;
  }
  var linkEl = document.createElement("a");
  linkEl.className = "psykhe-olivela-queries-heading__link";
  linkEl.href = href;
  linkEl.textContent = "VIEW ALL";
  row.appendChild(linkEl);
}

function syncOlivelaLabel(input, labelEl, hasResults) {
  if (!(labelEl instanceof HTMLElement)) return;
  var raw = typeof input.value === "string" ? input.value : "";
  var query = raw.trim();
  labelEl.textContent = (query && hasResults) ? "Suggestions" : "Most Popular Searches";
}

// Slot the "Most Popular Searches" heading + (conditional) "no results"
// notice into the search input's public `queries-heading` slot. Pure light-DOM
// append — no shadow DOM probe, no MutationObserver. The wrapper is
// idempotent via the `[slot="queries-heading"]` existence check.
function mountOlivelaQueriesHeading(input) {
  if (!(input instanceof HTMLElement)) return;
  if (input.querySelector(':scope > [slot="queries-heading"]')) return;

  var heading = document.createElement("div");
  heading.setAttribute("slot", "queries-heading");
  heading.className = "psykhe-olivela-queries-heading";

  var noResultsEl = document.createElement("div");
  noResultsEl.className = "psykhe-olivela-no-results";
  noResultsEl.hidden = true;
  noResultsEl.appendChild(document.createTextNode('Sorry, there are no results for "'));
  var noResultsQ = document.createElement("span");
  noResultsQ.className = "psykhe-olivela-no-results__q";
  noResultsEl.appendChild(noResultsQ);
  noResultsEl.appendChild(document.createTextNode('"'));
  heading.appendChild(noResultsEl);

  var labelRow = document.createElement("div");
  labelRow.className = "psykhe-olivela-queries-heading__row";

  var labelEl = document.createElement("span");
  labelEl.className = "psykhe-olivela-queries-heading__label";
  labelEl.textContent = "Most Popular Searches";
  labelRow.appendChild(labelEl);
  heading.appendChild(labelRow);

  input.appendChild(heading);
  input.__psykheOlivelaLabelEl = labelEl;
  input.__psykheOlivelaLabelRow = labelRow;
  // Initial state: empty query, no results, hide VIEW ALL.
  syncOlivelaViewAllLink(input, labelRow, false);
  syncOlivelaLabel(input, labelEl, false);
}

// Toggle the "Sorry, there are no results for '<q>'" notice that lives
// inside the slotted queries-heading. Shown only when user typed AND zero
// hits; hidden otherwise.
function setOlivelaNoResultsState(input, show, query) {
  if (!(input instanceof HTMLElement)) return;
  var noResultsEl = input.querySelector(
    ':scope > [slot="queries-heading"] .psykhe-olivela-no-results'
  );
  if (!noResultsEl) return;
  noResultsEl.hidden = !show;
  if (show) {
    var qEl = noResultsEl.querySelector(".psykhe-olivela-no-results__q");
    if (qEl) qEl.textContent = typeof query === "string" ? query : "";
  }
}

// Render a chip as an `<a>` so click/cmd-click/right-click navigate to
// `/search?q=...` natively. `@mousedown=preventDefault` preserves input focus
// so a click keeps the suggestions panel open until navigation commits.
function renderOlivelaQueryChipAsLink(query, ctx) {
  var value = (query && (query.value || query.label)) || "";
  var url =
    typeof query.url === "string" && query.url
      ? query.url
      : "/search?q=" + encodeURIComponent(value);
  return ctx.html`
    <a
      part="query-chip"
      href=${url}
      @mousedown=${(e) => e.preventDefault()}
    >${query.label || query.value}</a>
  `;
}

function olivelaSlugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function resolveOlivelaCollectionHref(label, preferredSlugs) {
  var table = Array.isArray(window.shopifyCollections) ? window.shopifyCollections : [];
  if (!table.length) return null;

  var candidates = [];
  for (var i = 0; i < preferredSlugs.length; i++) {
    var s = preferredSlugs[i];
    if (s) candidates.push(s);
  }
  var labelSlug = olivelaSlugify(label);
  if (labelSlug) candidates.push(labelSlug);

  var labelLower = String(label || "").toLowerCase().trim();

  for (var j = 0; j < table.length; j++) {
    var entry = table[j];
    if (!entry) continue;
    var handle = entry.handle || "";
    var title = (entry.title || "").toLowerCase().trim();
    if (title && title === labelLower) return "/collections/" + handle;
    for (var k = 0; k < candidates.length; k++) {
      if (handle === candidates[k]) return "/collections/" + handle;
    }
  }
  return null;
}

function deriveOlivelaChipsFromProducts(products, limit) {
  var max = typeof limit === "number" && limit > 0 ? limit : 15;
  if (!Array.isArray(products) || products.length === 0) return [];

  var chipMap = new Map();

  function upsertChip(label, url) {
    var key = label.toLowerCase();
    var existing = chipMap.get(key);
    if (!existing) {
      chipMap.set(key, { label: label, url: url });
    } else if (
      url.indexOf("/collections/") === 0 &&
      existing.url.indexOf("/collections/") !== 0
    ) {
      chipMap.set(key, { label: label, url: url });
    }
  }

  products.forEach(function (product) {
    if (!product || typeof product !== "object") return;
    var data = product.data;
    if (!data || typeof data !== "object") return;

    var isGiftCard = typeof data.product_type === "string"
      && data.product_type.trim().toLowerCase() === "gift cards";

    if (isGiftCard) {
      if (typeof data.handle === "string" && data.handle.trim()) {
        var giftLabel = (typeof data.title === "string" && data.title.trim()) || "Gift Card";
        upsertChip(giftLabel, "/products/" + data.handle.trim());
      }
    } else if (typeof data.vendor === "string" && data.vendor.trim()) {
      var vendorName = data.vendor.trim();
      var vendorSlug = olivelaSlugify(vendorName);
      var preferredSlugs = ["designer-" + vendorSlug, vendorSlug];
      var vendorUrl = resolveOlivelaCollectionHref(vendorName, preferredSlugs)
        || "/search?q=" + encodeURIComponent(vendorName);
      upsertChip(vendorName, vendorUrl);
    }

    var named = data.named_tags;
    if (!named || typeof named !== "object") return;

    function processLvl(arr, takeLast, levelKey) {
      if (!Array.isArray(arr)) return;
      arr.forEach(function (v) {
        if (typeof v !== "string") return;
        var raw = v.trim();
        if (!raw) return;
        var chipLabel = takeLast ? raw.split(">").pop().trim() : raw;
        if (!chipLabel) return;
        var chipSlug = olivelaSlugify(chipLabel);

        var chipUrl = null;
        if (levelKey === "category_2") {
          var segs = raw.split(">").map(function (s) { return s.trim(); }).filter(Boolean);
          if (segs.length >= 2) {
            var parentLabel = segs[segs.length - 2];
            var parentHref = resolveOlivelaCollectionHref(
              parentLabel,
              [olivelaSlugify(parentLabel)]
            );
            if (parentHref && parentHref.indexOf("/collections/") === 0) {
              chipUrl = parentHref + "?pf.category_2=" + encodeURIComponent(raw);
            }
          }
        }

        chipUrl = chipUrl
          || resolveOlivelaCollectionHref(chipLabel, [chipSlug])
          || "/search?q=" + encodeURIComponent(chipLabel);
        upsertChip(chipLabel, chipUrl);
      });
    }

    processLvl(named["categories.lvl0"], false, "category_0");
    processLvl(named["categories.lvl1"], true, "category_1");
    processLvl(named["categories.lvl2"], true, "category_2");
  });

  var chips = Array.from(chipMap.values())
    .sort(function (a, b) {
      return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : a.label.toLowerCase() > b.label.toLowerCase() ? 1 : 0;
    })
    .slice(0, max)
    .map(function (c) { return { value: c.label, label: c.label, url: c.url }; });

  return chips;
}

function applyOlivelaPublicOverrides(root) {
  var scope = root instanceof HTMLElement ? root : document;

  scope.querySelectorAll("psykhe-product-grid").forEach(function (grid) {
    if (!(grid instanceof HTMLElement)) {
      return;
    }
    grid.renderCard = function (product, ctx) {
      return renderOlivelaProductCard(product, ctx, ctx.actions.click(product));
    };
      // Customize empty-state message via slot="empty" override
    if (!grid.querySelector('[slot="empty"]')) {
      var emptySlot = document.createElement("div");
      emptySlot.setAttribute("slot", "empty");
      emptySlot.setAttribute("part", "empty-query");
      emptySlot.style.cssText = "text-align:center;padding:clamp(3rem,6vw,6rem) 1rem;font-size:16px;line-height:1.5;color:rgb(47,47,48);text-wrap:balance;";
      emptySlot.textContent = "Sorry, we couldn\u2019t find any results.";
      grid.appendChild(emptySlot);

      // Listen for query changes to update the message with the search term
      var provider = grid.closest("psykhe-provider") || grid.closest(".psykhe--search, .psykhe");
      if (provider) {
        provider.addEventListener("psykhe-query-change", function (e) {
          var q = (e.detail && e.detail.query) || "";
          emptySlot.textContent = q
            ? 'Sorry, we couldn\u2019t find any results for \u201C' + q + '\u201D'
            : "Sorry, we couldn\u2019t find any results.";
        });
      }

      // Also try to pick up the query from the URL for search pages
      var params = new URLSearchParams(window.location.search);
      var initialQ = params.get("q") || params.get("query") || "";
      if (initialQ) {
        emptySlot.textContent = 'Sorry, we couldn\u2019t find any results for \u201C' + initialQ + '\u201D';
      }
    }
  });

  scope.querySelectorAll("psykhe-recommendations").forEach(function (feed) {
    if (!(feed instanceof HTMLElement)) {
      return;
    }
    feed.renderCard = function (product, ctx) {
      return renderOlivelaProductCard(product, ctx, ctx.actions.click(product));
    };
  });

  scope.querySelectorAll("psykhe-active-filters").forEach(function (active) {
    if (!(active instanceof HTMLElement)) {
      return;
    }
    active.renderChip = renderOlivelaFilterChip;
  });

  scope.querySelectorAll("psykhe-filter-panel").forEach(function (panel) {
    applyOlivelaFilterPanelConfig(panel);
  });

  scope.querySelectorAll("psykhe-clear-filters").forEach(function (clearFilters) {
    if (
      !(clearFilters instanceof HTMLElement) ||
      clearFilters[WRAPPED_CLEAR_FILTERS_MARKER] ||
      typeof clearFilters.clearAll !== "function"
    ) {
      return;
    }
    var originalClearAll = clearFilters.clearAll.bind(clearFilters);
    clearFilters[WRAPPED_CLEAR_FILTERS_MARKER] = true;
    clearFilters.clearAll = function () {
      olivelaAutoOpenState.pendingDrillOpenKey = null;
      olivelaResetPriceRangeControls(document);
      return originalClearAll();
    };
  });

  scope.querySelectorAll("psykhe-search-input").forEach(function (input) {
    if (!(input instanceof HTMLElement)) return;

    if (input.suggestionsFetcher && input.suggestionsFetcher[WRAPPED_FETCHER_MARKER]) return;

    input.renderSuggestion = function (suggestion, ctx) {
      var product = suggestion && suggestion.data ? suggestion.data : null;
      if (!product) return ctx.nothing;
      var props = ctx.a11y && ctx.a11y.getOptionProps
        ? ctx.a11y.getOptionProps(suggestion)
        : null;
      var onClick = function () {
        ctx.actions.selectSuggestion(suggestion);
      };
      return ctx.html`
        <li
          id=${props ? props.id : ctx.nothing}
          part="suggestion"
          role=${props ? props.role : "option"}
          aria-selected=${props ? props["aria-selected"] : ctx.nothing}
          @mousedown=${function (e) { e.preventDefault(); }}
        >
          ${renderOlivelaProductCard(product, ctx, onClick, { linkTabIndex: -1 })}
        </li>
      `;
    };

    var originalFetcher = input.suggestionsFetcher;

    function fetchProducts(q, opts) {
      if (!originalFetcher) return Promise.resolve([]);
      return Promise.resolve(originalFetcher(q, opts)).then(function (res) {
        if (Array.isArray(res)) return res;
        return res && Array.isArray(res.products) ? res.products : [];
      });
    }

    if (!input.__psykheOlivelaQueryStatus) input.__psykheOlivelaQueryStatus = Object.create(null);

    var wrapper = function (query, options) {
      var trimmed = (query || "").trim();

      return fetchProducts(trimmed, options).then(function (products) {
        var labelEl = input.__psykheOlivelaLabelEl || null;
        var labelRow = input.__psykheOlivelaLabelRow || null;

        if (!trimmed) {
          // Empty input: show "Most Popular Searches" and hide VIEW ALL.
          setOlivelaNoResultsState(input, false);
          syncOlivelaLabel(input, labelEl, false);
          syncOlivelaViewAllLink(input, labelRow, false);
          var chips = deriveOlivelaChipsFromProducts(products, 15);
          return {
            queries: chips.length ? chips : OLIVELA_TRENDING_CHIPS,
            products: products,
          };
        }

        if (products.length === 0) {
          // Typed query with no matches: show the no-results notice,
          // keep the label as "Most Popular Searches", and hide VIEW ALL
          // so we never link shoppers to an empty SERP.
          input.__psykheOlivelaQueryStatus[trimmed] = false;
          return fetchProducts("", options).then(function (fallback) {
            setOlivelaNoResultsState(input, true, trimmed);
            syncOlivelaLabel(input, labelEl, false);
            syncOlivelaViewAllLink(input, labelRow, false);
            var fallbackChips = deriveOlivelaChipsFromProducts(fallback, 15);
            return {
              queries: fallbackChips.length ? fallbackChips : OLIVELA_TRENDING_CHIPS,
              products: fallback,
            };
          });
        }

        // Typed query with matches: show "Suggestions" + VIEW ALL.
        input.__psykheOlivelaQueryStatus[trimmed] = true;
        setOlivelaNoResultsState(input, false);
        syncOlivelaLabel(input, labelEl, true);
        syncOlivelaViewAllLink(input, labelRow, true);
        var chipsTyped = deriveOlivelaChipsFromProducts(products, 15);
        return {
          queries: chipsTyped.length ? chipsTyped : OLIVELA_TRENDING_CHIPS,
          products: products,
        };
      });
    };
    wrapper[WRAPPED_FETCHER_MARKER] = true;
    input.suggestionsFetcher = wrapper;

    input.minQueryLength = 0;
    input.setAttribute("min-query-length", "0");

    input.productsHeading = "Products";
    input.queriesHeading = "Most Popular Searches";
    input.renderQueryChip = renderOlivelaQueryChipAsLink;

    if (input.dataset.psykheOlivelaClickGuardWired !== "true") {
      input.dataset.psykheOlivelaClickGuardWired = "true";
      var stopProp = function (e) {
        e.stopPropagation();
      };
      input.addEventListener("pointerdown", stopProp);
      input.addEventListener("click", stopProp);
    }

    // Replace the SDK's default "×" glyph with our "CLEAR" text label by
    // slotting into the component's public `clear-icon` slot. The button
    // itself is rendered conditionally by the SDK only when the input has
    // a value, so no extra show/hide wiring is needed here.
    if (!input.querySelector(':scope > [slot="clear-icon"]')) {
      var clearLabelEl = document.createElement("span");
      clearLabelEl.setAttribute("slot", "clear-icon");
      clearLabelEl.className = "psykhe-olivela-clear-label";
      clearLabelEl.textContent = "Clear";
      input.appendChild(clearLabelEl);
    }

    mountOlivelaQueriesHeading(input);

    // Wire CLEAR button click cleanup. The SDK's handleClear refocuses
    // the already-focused input on macOS, which does not fire a fresh
    // `focus` event — so the fetcher never re-runs and VIEW ALL / label
    // state would stay stale. We listen on the host, detect a click on
    // [part="clear"] via composedPath, briefly blur the shadow input so
    // the SDK's queued refocus fires a real focus event (reopening the
    // trending-chip panel), and scrub any leftover heading state
    // synchronously so nothing flashes between re-renders.
    if (input.dataset.psykheOlivelaClearWired !== "true") {
      input.dataset.psykheOlivelaClearWired = "true";
      input.addEventListener("click", function (ev) {
        var path = typeof ev.composedPath === "function" ? ev.composedPath() : [];
        var hitClear = false;
        for (var i = 0; i < path.length; i++) {
          var node = path[i];
          if (
            node &&
            node.getAttribute &&
            node.getAttribute("part") === "clear"
          ) {
            hitClear = true;
            break;
          }
        }
        if (!hitClear) return;

        var shadowInput = input.shadowRoot
          ? input.shadowRoot.querySelector('[part="input"]')
          : null;
        if (shadowInput) shadowInput.blur();

        var labelElLocal = input.__psykheOlivelaLabelEl || null;
        var labelRowLocal = input.__psykheOlivelaLabelRow || null;
        syncOlivelaViewAllLink(input, labelRowLocal, false);
        syncOlivelaLabel(input, labelElLocal, false);
        setOlivelaNoResultsState(input, false);
      });
    }

    // Keep VIEW ALL / label / no-results state aligned with whatever
    // suggestion list the search input is showing right now. The fetcher
    // records each query's hit/miss outcome in a map; this listener reads
    // that map whenever the resolved suggestion list changes and re-applies
    // the matching heading state. Empty queries scrub back to the default
    // "Most Popular Searches" state.
    if (input.dataset.psykheOlivelaSuggestionsSyncWired !== "true") {
      input.dataset.psykheOlivelaSuggestionsSyncWired = "true";
      input.addEventListener("psykhe-suggestions-change", function (ev) {
        var query = ev && ev.detail && typeof ev.detail.query === "string"
          ? ev.detail.query
          : "";
        var labelEl = input.__psykheOlivelaLabelEl || null;
        var labelRow = input.__psykheOlivelaLabelRow || null;
        if (!query) {
          syncOlivelaViewAllLink(input, labelRow, false);
          syncOlivelaLabel(input, labelEl, false);
          setOlivelaNoResultsState(input, false);
          return;
        }
        var status = input.__psykheOlivelaQueryStatus;
        if (!status || !Object.prototype.hasOwnProperty.call(status, query)) return;
        var hasProducts = status[query] === true;
        syncOlivelaViewAllLink(input, labelRow, hasProducts);
        syncOlivelaLabel(input, labelEl, hasProducts);
        setOlivelaNoResultsState(input, !hasProducts, query);
      });
    }

    // Reset heading + query state when the search modal closes, so the
    // next open never shows a stale VIEW ALL / label from the previous
    // session. The theme reassigns the native input value on close
    // without dispatching `input`, so we cannot rely on DOM events.
    if (input.dataset.psykheOlivelaDialogCloseWired !== "true") {
      var dialog = input.closest("dialog");
      if (dialog) {
        input.dataset.psykheOlivelaDialogCloseWired = "true";
        dialog.addEventListener("close", function () {
          if (typeof input.reset === "function") input.reset();
          var labelEl = input.__psykheOlivelaLabelEl || null;
          var labelRow = input.__psykheOlivelaLabelRow || null;
          syncOlivelaViewAllLink(input, labelRow, false);
          setOlivelaNoResultsState(input, false);
          syncOlivelaLabel(input, labelEl, false);
        });
      }
    }
  });
}

// ---------------------------------------------------------------------------
// 5. PAGINATION POLISH
//
// Hide pagination entirely when there is only one page of results. Olivela
// uses standard page-mode pagination, so we can derive the host visibility
// from the public results payload and let the component's built-in
// `hide-on-single-page` support handle the inner controls.
// ---------------------------------------------------------------------------

function shouldHideOlivelaPagination() {
  if (olivelaRuntimeState.page > 1) {
    return false;
  }

  if (olivelaRuntimeState.total === 0) {
    return true;
  }

  if (olivelaRuntimeState.itemsOnPage <= 0) {
    return false;
  }

  return olivelaRuntimeState.total <= olivelaRuntimeState.itemsOnPage;
}

function applyOlivelaPaginationConfig(pagination) {
  if (!(pagination instanceof HTMLElement)) {
    return;
  }

  pagination.toggleAttribute("hide-on-single-page", true);
  pagination.toggleAttribute("hide-disabled-nav", true);

  if ("showNavTooltips" in pagination) {
    pagination.showNavTooltips = true;
  }
  pagination.toggleAttribute("show-nav-tooltips", true);
}

function syncOlivelaPaginationVisibility(root) {
  var scope = root instanceof HTMLElement ? root : document;
  var shouldHide = shouldHideOlivelaPagination();

  scope.querySelectorAll("psykhe-pagination").forEach(function (pagination) {
    if (!(pagination instanceof HTMLElement)) {
      return;
    }

    applyOlivelaPaginationConfig(pagination);

    var hidePageModePagination =
      pagination.getAttribute("mode") === "page" && shouldHide;
    pagination.classList.toggle(
      "psykhe-pagination-hidden",
      hidePageModePagination
    );

    var appBlock = pagination.closest(".shopify-app-block");
    if (appBlock instanceof HTMLElement) {
      appBlock.classList.toggle(
        "psykhe-app-block--with-pagination",
        !hidePageModePagination
      );
    }
  });
}

// ---------------------------------------------------------------------------
// 5b. PAGINATION / FILTER SCROLL CONTRACT (reference only)
//
// psykhe-pagination owns the scroll on page/filter change. The extension
// ships `scroll-to="#psykhe-results-top"` as the default target and
// `--psykhe-scroll-offset` as the public CSS var for sticky-header offset.
// The offset is tuned in assets/psykhe.css — no JS needed here.
//
//
//   // Retarget: scroll to a different element
//   document.querySelectorAll("psykhe-pagination").forEach(function (el) {
//     el.setAttribute("scroll-to", ".my-results-wrapper");
//   });
//
//   // Scroll to document top instead of results
//   document.querySelectorAll("psykhe-pagination").forEach(function (el) {
//     el.setAttribute("scroll-to", "body");
//   });
//
//   // Disable the built-in scroll entirely
//   document.querySelectorAll("psykhe-pagination").forEach(function (el) {
//     el.removeAttribute("scroll-to");
//   });
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// 6. STICKY CONTROLS BAR
//
// Makes the controls bar and applied-filters bar stick below the site header
// on scroll. Mirrors plp-sticky-filter.js runStickyFilterBarAndPills():
// sentinel + IntersectionObserver + class toggling + placeholders.
// ---------------------------------------------------------------------------

// Guard path must stay cheap because this can run multiple times per page as
// providers and theme chrome initialize.
function initStickyControls(root) {
  if (!(root instanceof HTMLElement)) return;
  if (root.dataset.psykheOlivelaStickyInit === "true") return;

  var controls = root.querySelector(".psykhe__controls");
  if (!(controls instanceof HTMLElement)) return;

  var pillsBar = root.querySelector(".psykhe-applied-filters-bar");

  root.dataset.psykheOlivelaStickyInit = "true";

  var sentinel = document.createElement("div");
  sentinel.style.width = "100%";
  sentinel.style.height = "1px";
  controls.parentNode.insertBefore(sentinel, controls);

  var placeholder = null;
  var pillsPlaceholder = null;

  function ensurePlaceholder() {
    if (!placeholder) {
      placeholder = document.createElement("div");
      placeholder.className = "psykhe-sticky-placeholder";
      controls.parentNode.insertBefore(placeholder, controls);
    }
    placeholder.style.height = controls.offsetHeight + "px";
  }

  function removePlaceholder() {
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.removeChild(placeholder);
      placeholder = null;
    }
  }

  function ensurePillsPlaceholder() {
    if (!pillsBar) pillsBar = root.querySelector(".psykhe-applied-filters-bar");
    if (!pillsBar) return;
    if (!pillsPlaceholder) {
      pillsPlaceholder = document.createElement("div");
      pillsPlaceholder.className = "psykhe-applied-filters-placeholder";
      pillsBar.parentNode.insertBefore(pillsPlaceholder, pillsBar);
    }
    pillsPlaceholder.style.height = pillsBar.offsetHeight + "px";
  }

  function removePillsPlaceholder() {
    if (pillsPlaceholder && pillsPlaceholder.parentNode) {
      pillsPlaceholder.parentNode.removeChild(pillsPlaceholder);
      pillsPlaceholder = null;
    }
  }

  function pillsHasContent() {
    if (!pillsBar) pillsBar = root.querySelector(".psykhe-applied-filters-bar");
    if (!pillsBar) return false;
    var af = pillsBar.querySelector("psykhe-active-filters");
    // hasFilters is a live property on psykhe-active-filters; falls back to
    // the count of items in its state if the property is missing.
    if (!af) return false;
    if (typeof af.hasFilters === "boolean") return af.hasFilters;
    var items = af.state && Array.isArray(af.state.items) ? af.state.items : [];
    return items.length > 0;
  }

  // Toggle the .psykhe-has-filters class on the bar so CSS can show/hide it.
  // Without this class the bar stays display:none and doesn't take up space.
  function syncPillsBarVisibility() {
    if (!pillsBar) pillsBar = root.querySelector(".psykhe-applied-filters-bar");
    if (!pillsBar) return;
    if (pillsHasContent()) {
      pillsBar.classList.add("psykhe-has-filters");
    } else {
      pillsBar.classList.remove("psykhe-has-filters");
    }
  }

  var observer = null;

  function updateSticky() {
    syncPillsBarVisibility();
    var stickyOffset = getStickyOffset();
    root.style.setProperty("--psykhe-sticky-offset", stickyOffset + "px");

    var entryTop = sentinel.getBoundingClientRect().top;
    if (entryTop < stickyOffset) {
      ensurePlaceholder();
      controls.classList.add("psykhe-is-fixed");
      controls.classList.add("psykhe-is-stuck");

      // Position the pills bar right below the fixed controls bar.
      var controlsBottom = stickyOffset + controls.offsetHeight;
      root.style.setProperty("--psykhe-pills-fixed-top", controlsBottom + "px");
      if (pillsBar) pillsBar.style.setProperty("--psykhe-pills-fixed-top", controlsBottom + "px");

      if (pillsHasContent()) {
        ensurePillsPlaceholder();
        pillsBar.classList.add("psykhe-is-fixed");
        controls.classList.add("psykhe-with-pills");
      } else {
        if (pillsBar) pillsBar.classList.remove("psykhe-is-fixed");
        removePillsPlaceholder();
        controls.classList.remove("psykhe-with-pills");
      }
    } else {
      controls.classList.remove("psykhe-is-fixed");
      controls.classList.remove("psykhe-is-stuck");
      removePlaceholder();
      if (pillsBar) pillsBar.classList.remove("psykhe-is-fixed");
      removePillsPlaceholder();
      controls.classList.remove("psykhe-with-pills");
    }
  }

  function createObserver() {
    if (observer) observer.disconnect();
    var stickyOffset = getStickyOffset();
    observer = new IntersectionObserver(
      function () { updateSticky(); },
      { root: null, threshold: [0], rootMargin: "-" + stickyOffset + "px 0px 0px 0px" }
    );
    observer.observe(sentinel);
  }
  createObserver();

  // Watch for filter changes via the component's own event (bubbles, composed).
  // Using the event avoids a MutationObserver feedback loop since we toggle
  // classes on pillsBar inside updateSticky().
  root.addEventListener("psykhe-active-filters-change", function () {
    updateSticky();
  });

  window.addEventListener("resize", function () {
    if (controls.classList.contains("psykhe-is-fixed") && placeholder) {
      placeholder.style.height = controls.offsetHeight + "px";
    }
    if (pillsBar && pillsBar.classList.contains("psykhe-is-fixed") && pillsPlaceholder) {
      pillsPlaceholder.style.height = pillsBar.offsetHeight + "px";
    }
    createObserver();
    updateSticky();
  });

  // Keep the sticky offset in sync while the header height changes during
  // scroll (announcement-bar dismissal, sticky-show transition). rAF throttle
  // keeps this cheap; updateSticky() only mutates DOM when classes actually
  // need to flip.
  var _scrollRaf = 0;
  window.addEventListener(
    "scroll",
    function () {
      if (_scrollRaf) return;
      _scrollRaf = window.requestAnimationFrame(function () {
        _scrollRaf = 0;
        updateSticky();
      });
    },
    { passive: true }
  );

  updateSticky();
}

function getOlivelaProviderInstanceForDrawer(drawer) {
  if (!(drawer instanceof HTMLElement)) {
    return "";
  }

  const cachedInstance = String(
    drawer.dataset.psykheOlivelaInstance || ""
  ).trim();
  if (cachedInstance) {
    return cachedInstance;
  }

  const provider = drawer.closest("psykhe-provider");
  const providerInstance = String(
    provider?.getAttribute("instance") || provider?.instance || ""
  ).trim();
  if (providerInstance) {
    drawer.dataset.psykheOlivelaInstance = providerInstance;
    return providerInstance;
  }

  const triggerSelector = String(
    drawer.getAttribute("trigger") || drawer.trigger || ""
  ).trim();
  if (!triggerSelector) {
    return "";
  }

  let triggerElement = null;
  try {
    triggerElement = drawer.ownerDocument.querySelector(triggerSelector);
  } catch {
    return "";
  }

  const triggerProvider = triggerElement?.closest("psykhe-provider");
  const triggerProviderInstance = String(
    triggerProvider?.getAttribute("instance") || triggerProvider?.instance || ""
  ).trim();
  if (triggerProviderInstance) {
    drawer.dataset.psykheOlivelaInstance = triggerProviderInstance;
  }

  return triggerProviderInstance;
}

function syncOlivelaDrawerContext(drawer) {
  if (!(drawer instanceof HTMLElement)) {
    return;
  }

  const providerInstance = getOlivelaProviderInstanceForDrawer(drawer);
  if (!providerInstance) {
    return;
  }

  drawer
    .querySelectorAll(
      "psykhe-active-filters, psykhe-clear-filters, psykhe-filter-panel, psykhe-results-count"
    )
    .forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      if ((element.getAttribute("instance") || "").trim() !== providerInstance) {
        element.setAttribute("instance", providerInstance);
      }
    });
}

function initOlivelaDrawer(drawer) {
  if (!(drawer instanceof HTMLElement)) return;
  if (drawer.dataset.psykheOlivelaDrawerInit !== "true") {
    const header = document.createElement("div");
    header.slot = "header";
    header.className = "psykhe-drawer-header";
    header.innerHTML = `
      <span class="psykhe-drawer-title">Filter</span>
      <div class="psykhe-drawer-header-right">
        <psykhe-results-count class="psykhe-drawer-results"></psykhe-results-count>
        <button type="button" class="psykhe-drawer-close" aria-label="Close filter drawer">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="1" y1="1" x2="9" y2="9" stroke="#222" stroke-width="0.8" stroke-linecap="round"/>
            <line x1="9" y1="1" x2="1" y2="9" stroke="#222" stroke-width="0.8" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;
    drawer.prepend(header);

    var closeBtn = header.querySelector(".psykhe-drawer-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", function () {
        drawer.removeAttribute("open");
      });
    }

    drawer.dataset.psykheOlivelaDrawerInit = "true";
  }

  syncOlivelaDrawerContext(drawer);

  drawer.querySelectorAll("psykhe-clear-filters").forEach(function (element) {
    if (!(element instanceof HTMLElement)) {
      return;
    }
    if ("hideWhenEmpty" in element) {
      element.hideWhenEmpty = false;
    }
    if (element.getAttribute("hide-when-empty") !== "false") {
      element.setAttribute("hide-when-empty", "false");
    }
  });

  // Footer elements (psykhe-clear-filters + view button) ship as direct
  // children of <psykhe-drawer> with slot="footer" — no DOM-move needed.
}

// ---------------------------------------------------------------------------
// 7. FILTER CONFIG, CONTENT TILES, AND ANALYTICS
//
// Theme-owned runtime glue for filter config, content tiles, and analytics.
// Filter customization lives in `.facets = [...]`, content-tile visibility
// tracks public result events, and analytics listens to typed `psykhe-*`
// events instead of DOM click reverse-mapping.
// ---------------------------------------------------------------------------

// Color map for Olivela's facet palette. Covers values from the theme's
// color-constants.js plus additional entries that appear in live facet
// data (e.g. Maroon, Beige) so swatches always resolve.
const olivelaColorMap = {
  Silver: "#c0c0c0",
  Black: "#000000",
  Multicolor: "#e5e5e5",
  White: "#ffffff",
  Blue: "#4146AB",
  Pink: "#E896CA",
  Green: "#087B3C",
  Brown: "#643B37",
  Tan: "#d2b48c",
  Gold: "#ffd700",
  Grey: "#979797",
  Metallic: "#B6A489",
  Neutrals: "#D6C5B9",
  Neutral: "#D6C5B9",
  Orange: "#E17C54",
  Purple: "#8718B4",
  Red: "#CF1818",
  Yellow: "#FFDC4A",
  Maroon: "#800000",
  Beige: "#F5F5DC",
  Cream: "#FFFDD0",
  Ivory: "#FFFFF0",
  Nude: "#E3BC9A",
  Navy: "#000080",
  Olive: "#808000",
  Burgundy: "#800020",
  Rose: "#FF007F",
  Copper: "#B87333",
  Bronze: "#CD7F32",
  Khaki: "#C3B091",
  Turquoise: "#40E0D0",
  Teal: "#008080",
  Lavender: "#B57EDC",
  Coral: "#FF7F50",
  Mustard: "#FFDB58",
};

function normalizeOlivelaHierarchicalLabel(value) {
  if (typeof value !== "string") {
    return "";
  }

  var segments = value
    .split(">")
    .map(function (segment) {
      return segment.trim();
    })
    .filter(function (segment) {
      return segment.length > 0;
    });

  if (segments.length === 0) {
    return value.trim();
  }

  return segments[segments.length - 1];
}

function getOlivelaCollectionTitle() {
  var decoder = document.createElement("textarea");
  decoder.innerHTML = String(window.COLLECTION_TITLE || "").trim();
  return decoder.value.trim();
}

function getOlivelaEventInstance(event, detail) {
  var explicitInstance =
    detail && typeof detail.instance === "string" ? detail.instance.trim() : "";
  if (explicitInstance) {
    return explicitInstance;
  }

  var target = event.target;
  if (!(target instanceof HTMLElement)) {
    return "";
  }

  var targetInstance = String(
    target.getAttribute("instance") || target.instance || ""
  ).trim();
  if (targetInstance) {
    return targetInstance;
  }

  var provider = target.closest("psykhe-provider");
  return String(
    provider?.getAttribute("instance") || provider?.instance || ""
  ).trim();
}

function isOlivelaPlpEvent(event, detail) {
  return getOlivelaEventInstance(event, detail) === olivelaRuntimeState.instance;
}

var OLIVELA_CONTENT_TILE_DEFINITIONS = [
  {
    globalName: "contentTileOne",
    minTotal: 12,
    tile: {
      id: "olivela-tile-one",
      slot: "olivela-tile-one",
      beforeProductIndex: 6,
      colSpan: 2,
    },
  },
  {
    globalName: "contentTileTwo",
    minTotal: 20,
    tile: {
      id: "olivela-tile-two",
      slot: "olivela-tile-two",
      beforeProductIndex: 18,
      colSpan: 2,
    },
  },
];

function normalizeOlivelaContentTileHtml(html) {
  if (typeof html !== "string") {
    return "";
  }

  var trimmed = html.trim();
  if (trimmed === "") {
    return "";
  }

  var container = document.createElement("div");
  container.innerHTML = trimmed;
  container.querySelectorAll("script").forEach(function (node) {
    node.remove();
  });

  var contentChildren = Array.from(container.children);
  var builderComponent =
    contentChildren.length === 1 &&
    contentChildren[0].tagName === "BUILDER-COMPONENT"
      ? contentChildren[0]
      : null;
  var hasPlaceholderOnly =
    builderComponent &&
    builderComponent.getAttribute("editing-only") === "true" &&
    builderComponent.querySelector("*") === null &&
    container.textContent.trim() === "";

  return hasPlaceholderOnly ? "" : trimmed;
}

function getOlivelaContentTileHtml(globalName) {
  return normalizeOlivelaContentTileHtml(window[globalName]);
}

// Resolves the PLP grid for the active provider instance. Tries a direct
// attribute selector first, then a provider-scoped descendant lookup, so
// Builder.io editorial tiles resolve regardless of which element carries
// the instance attribute.
function getOlivelaContentTileGrid(root) {
  var scope = root instanceof HTMLElement ? root : document;
  var instance = olivelaRuntimeState.instance;
  var directSelector = 'psykhe-product-grid[instance="' + instance + '"]';
  var providerScopedSelector =
    'psykhe-provider[instance="' + instance + '"] psykhe-product-grid';
  var grid =
    scope.querySelector(directSelector) ||
    document.querySelector(directSelector) ||
    scope.querySelector(providerScopedSelector) ||
    document.querySelector(providerScopedSelector);
  if (!(grid instanceof HTMLElement) || !("contentTiles" in grid)) {
    return null;
  }
  return grid;
}

function ensureOlivelaContentTileSlotChild(grid, slotName, html) {
  var existing = grid.querySelector(':scope > [slot="' + slotName + '"]');
  if (typeof html !== "string" || html.trim() === "") {
    if (existing instanceof HTMLElement) {
      existing.remove();
    }
    return false;
  }

  if (!(existing instanceof HTMLElement)) {
    existing = document.createElement("section");
    existing.setAttribute("slot", slotName);
    existing.className = "psykhe-content-tile";
    grid.appendChild(existing);
  }

  if (existing.innerHTML !== html) {
    existing.innerHTML = html;
  }

  return true;
}

function syncOlivelaContentTiles(root) {
  var grid = getOlivelaContentTileGrid(root);
  if (!(grid instanceof HTMLElement)) {
    return;
  }

  if (window.showPlpContentTiles === false) {
    grid.contentTiles = [];
    return;
  }

  var tiles = [];
  OLIVELA_CONTENT_TILE_DEFINITIONS.forEach(function (definition) {
    var tileHtml = getOlivelaContentTileHtml(definition.globalName);
    if (!ensureOlivelaContentTileSlotChild(grid, definition.tile.slot, tileHtml)) {
      return;
    }

    var hidden =
      olivelaRuntimeState.page > 1 ||
      olivelaRuntimeState.total < definition.minTotal;
    tiles.push({
      id: definition.tile.id,
      slot: definition.tile.slot,
      beforeProductIndex: definition.tile.beforeProductIndex,
      colSpan: definition.tile.colSpan,
      hidden: hidden,
    });
  });

  grid.contentTiles = tiles;
}

function handleOlivelaResultsUpdated(event) {
  var detail = event.detail || {};
  if (!isOlivelaPlpEvent(event, detail)) {
    return;
  }

  if (typeof detail.page === "number" && detail.page >= 1) {
    olivelaRuntimeState.page = detail.page;
  }
  if (typeof detail.total === "number" && detail.total >= 0) {
    olivelaRuntimeState.total = detail.total;
  }
  olivelaRuntimeState.itemsOnPage = Array.isArray(detail.items)
    ? detail.items.length
    : 0;

  syncOlivelaPaginationVisibility(document);
  syncOlivelaContentTiles(event.target);
}

function handleOlivelaRefinementsChange(event) {
  if (!isOlivelaPlpEvent(event)) {
    return;
  }

  var detail = event.detail || {};
  olivelaRuntimeState.facets = Array.isArray(detail.facets) ? detail.facets : [];

  var sourceFacets = olivelaRuntimeState.facets;
  var currCat0 = getSelectedFacetLabels(sourceFacets, "category_0");
  var currCat1 = getSelectedFacetLabels(sourceFacets, "category_1");
  var currCat2 = getSelectedFacetLabels(sourceFacets, "category_2");
  var mappingDepth = getOlivelaCollectionSegments().length;

  var cat0Added = currCat0.some(function (v) {
    return olivelaAutoOpenState.prevCat0Selected.indexOf(v) === -1;
  });
  var cat1Added = currCat1.some(function (v) {
    return olivelaAutoOpenState.prevCat1Selected.indexOf(v) === -1;
  });

  var cat1Lost = currCat1.length === 0
    && olivelaAutoOpenState.prevCat1Selected.length > 0;
  var cat2Lost = currCat2.length === 0
    && olivelaAutoOpenState.prevCat2Selected.length > 0;

  var keyToOpen = null;
  var isDrillUp = false;
  if (cat1Added && currCat2.length === 0) {
    keyToOpen = "category_2";
  } else if (cat0Added && currCat1.length === 0) {
    keyToOpen = "category_1";
  } else if (cat2Lost && (currCat1.length > 0 || mappingDepth >= 2)) {
    keyToOpen = "category_2";
    isDrillUp = true;
  } else if (cat1Lost && (currCat0.length > 0 || mappingDepth >= 1)) {
    keyToOpen = "category_1";
    isDrillUp = true;
  }

  olivelaAutoOpenState.prevCat0Selected = currCat0;
  olivelaAutoOpenState.prevCat1Selected = currCat1;
  olivelaAutoOpenState.prevCat2Selected = currCat2;

  applyOlivelaPublicOverrides(event.target);

  if (keyToOpen) {
    if (isDrillUp) {
      // Drill-up: user cleared a child level. Open immediately — the user is
      // already looking at the drawer and stale-data flash is acceptable since
      // the cleared facet just lost its values.
      var scope = event.target instanceof HTMLElement ? event.target : document;
      var drawer = getOlivelaPriorityFilterDrawer(scope);
      if (drawer && drawer.hasAttribute("open")) {
        openFacetInPriorityDrawer(drawer, keyToOpen);
      }
    } else {
      // Drill-down: user selected a parent value, child should open. Defer
      // until the browse response lands (psykhe-loading-change loading:false)
      // so the child accordion renders with fresh facet values, not stale ones
      // from the previous browse.
      olivelaAutoOpenState.pendingDrillOpenKey = keyToOpen;
    }
  }
}

// ---------------------------------------------------------------------------
// Hierarchical category cascade: when a parent category level loses ALL its
// selections (goes to 0 active), child levels are cleared automatically.
// Cascade only fires on full deselect — removing one of several values at the
// same level does NOT cascade. The hierarchy is:
//   category_0 → category_1 → category_2
// Examples:
//   - category_0 had 2 values, user removes 1 → 1 remains → no cascade
//   - category_0 had 1 value, user removes it → 0 remain → clear cat1 + cat2
//   - category_1 cleared via "clear" button → clear cat2
//
// Cascade is driven by two synchronous intent events:
//   psykhe-facet-change  — fired by psykhe-filter-panel (drawer interactions)
//   psykhe-filter-remove — fired by psykhe-active-filters (sticky bar chips)
// Both fire BEFORE the browse request, so child clears batch into one request.
// ---------------------------------------------------------------------------
var OLIVELA_CATEGORY_CHILDREN = {
  category_0: ["category_1", "category_2"],
  category_1: ["category_2"],
};

function getOlivelaProvider(event) {
  var target = event.target instanceof HTMLElement ? event.target : null;
  var provider = target && target.closest("psykhe-provider");
  return provider || document.querySelector("psykhe-provider");
}

function cascadeOlivelaCategoryClear(facetKey, event) {
  var children = OLIVELA_CATEGORY_CHILDREN[facetKey];
  if (!children) return;

  var provider = getOlivelaProvider(event);
  if (!provider || typeof provider.sdk?.clearFilter !== "function") return;

  for (var i = 0; i < children.length; i++) {
    provider.sdk.clearFilter(children[i]);
  }
}

function shouldOlivelaCategoryCascade(facetKey, removedValue) {
  var children = OLIVELA_CATEGORY_CHILDREN[facetKey];
  if (!children) return false;

  // olivelaRuntimeState.facets holds pre-mutation state (psykhe-facet-change
  // fires before the SDK commit). Manually exclude removedValue to simulate
  // the post-mutation count.
  var selected = getSelectedFacetLabels(olivelaRuntimeState.facets, facetKey);
  var remaining = selected.filter(function (v) {
    return String(v) !== String(removedValue);
  });
  return remaining.length === 0;
}

function handleOlivelaFacetChange(event) {
  var detail = event.detail || {};
  if (!isOlivelaPlpEvent(event, detail)) {
    return;
  }

  if (detail.selected === false
    && shouldOlivelaCategoryCascade(detail.facetKey, detail.value)) {
    cascadeOlivelaCategoryClear(detail.facetKey, event);
  }
}

function handleOlivelaFacetCleared(event) {
  var detail = event.detail || {};
  if (!isOlivelaPlpEvent(event, detail)) {
    return;
  }

  if (String(detail.facetKey || "").toLowerCase() === "price") {
    olivelaResetPriceRangeControls(document);
  }

  cascadeOlivelaCategoryClear(detail.facetKey, event);
}

function handleOlivelaFilterRemove(event) {
  var detail = event.detail || {};
  if (!isOlivelaPlpEvent(event, detail)) {
    return;
  }

  if (String(detail.facetKey || "").toLowerCase() === "price") {
    olivelaResetPriceRangeControls(document);
  }

  if (shouldOlivelaCategoryCascade(detail.facetKey, detail.value)) {
    cascadeOlivelaCategoryClear(detail.facetKey, event);
  }
}

function handleOlivelaFiltersCleared(event) {
  var detail = event.detail || {};
  if (!isOlivelaPlpEvent(event, detail)) {
    return;
  }

  olivelaAutoOpenState.pendingDrillOpenKey = null;
  olivelaResetPriceRangeControls(document);
}

function handleOlivelaClearFiltersClick(event) {
  var path =
    typeof event.composedPath === "function" ? event.composedPath() : [];
  var clickedClearFilters = path.some(function (entry) {
    return (
      entry &&
      entry.tagName &&
      String(entry.tagName).toLowerCase() === "psykhe-clear-filters"
    );
  });
  if (clickedClearFilters) {
    olivelaAutoOpenState.pendingDrillOpenKey = null;
    olivelaResetPriceRangeControls(document);
  }
}

function handleOlivelaClearFiltersStateChange(event) {
  var detail = event.detail || {};
  if (!isOlivelaPlpEvent(event, detail)) {
    return;
  }
  if (detail.hasFilters === false) {
    olivelaAutoOpenState.pendingDrillOpenKey = null;
    olivelaResetPriceRangeControls(document);
  }
}

// Flush deferred drill-down accordion open once the browse response settles.
// psykhe-loading-change fires with loading:false after updateStoresWithResponse
// has written fresh facets to the store, so the accordion renders with current
// values instead of stale ones from the previous browse.
function handleOlivelaLoadingChange(event) {
  if (!isOlivelaPlpEvent(event)) return;

  var detail = event.detail || {};
  if (detail.loading !== false) return;

  var keyToOpen = olivelaAutoOpenState.pendingDrillOpenKey;
  if (!keyToOpen) return;
  olivelaAutoOpenState.pendingDrillOpenKey = null;

  var scope = event.target instanceof HTMLElement ? event.target : document;
  var drawer = getOlivelaPriorityFilterDrawer(scope);
  openFacetInPriorityDrawer(drawer, keyToOpen);
}

function initOlivelaRuntimeBindings() {
  if (olivelaRuntimeState.runtimeInit) {
    return;
  }
  olivelaRuntimeState.runtimeInit = true;

  document.addEventListener("psykhe-results-updated", handleOlivelaResultsUpdated);
  document.addEventListener(
    "psykhe-refinements-change",
    handleOlivelaRefinementsChange
  );
  document.addEventListener("psykhe-facet-change", handleOlivelaFacetChange);
  document.addEventListener("psykhe-facet-cleared", handleOlivelaFacetCleared);
  document.addEventListener("psykhe-filter-remove", handleOlivelaFilterRemove);
  document.addEventListener(
    "psykhe-filters-cleared",
    handleOlivelaFiltersCleared
  );
  document.addEventListener(
    "psykhe-clear-filters-change",
    handleOlivelaClearFiltersStateChange
  );
  document.addEventListener("click", handleOlivelaClearFiltersClick, true);

  document.addEventListener("psykhe-loading-change", handleOlivelaLoadingChange);
}

function runOlivelaInitStep(label, callback) {
  try {
    callback();
  } catch (error) {
    console.error(`[psykhe-olivela] ${label} init failed`, error);
  }
}

// ---------------------------------------------------------------------------
// 8. CONTROLS BAR TEXT TWEAKS
//
// Matches the controls bar labels and count text to the theme layout:
//   - responsive "FILTER" / "All filters" text
//   - mobile sort shows "SORT" prefix instead of selected value
//   - stats format: "N Products" / "N Product"
// ---------------------------------------------------------------------------

function initOlivelaControlsBar(root) {
  var trigger = root.querySelector(".psykhe__drawer-trigger");
  if (trigger) {
    function setTriggerText() {
      var text = window.innerWidth <= 768 ? "FILTER" : "All filters";
      if (trigger.textContent !== text) trigger.textContent = text;
    }
    setTriggerText();
    if (root.dataset.psykheControlsBarInit !== "true") {
      window.addEventListener("resize", setTriggerText);
    }
  }

  var sort = root.querySelector("psykhe-sort-select");
  if (sort && sort.getAttribute("prefix") !== "SORT") {
    sort.setAttribute("prefix", "SORT");
  }
  if (sort && sort.getAttribute("icon") !== "›") {
    sort.setAttribute("icon", "›");
  }

  var resultsCount = root.querySelector("psykhe-results-count");
  if (resultsCount && resultsCount.getAttribute("plural-label") !== "Products") {
    resultsCount.setAttribute("singular-label", "Products");
    resultsCount.setAttribute("plural-label", "Products");
  }

  syncOlivelaPriorityFilters(root);

  if (root.dataset.psykheControlsBarInit !== "true") {
    window.addEventListener("resize", function () {
      syncOlivelaPriorityFilters(root);
    });
  }

  root.dataset.psykheControlsBarInit = "true";

  // Pagination: the component handles disabled-nav hiding and inner
  // single-page collapse via public attributes; theme JS only syncs the host
  // visibility class/margin from the public results payload.
}

function getOlivelaPriorityFilterDrawer(root) {
  if (!(root instanceof HTMLElement)) {
    return null;
  }

  return root.querySelector("psykhe-drawer.psykhe__filter-drawer");
}

function openFacetInPriorityDrawer(drawer, facetKey) {
  var panel = drawer && drawer.querySelector("psykhe-filter-panel");
  if (panel && typeof panel.openFacet === "function") {
    panel.openFacet(facetKey);
  }
}

function openOlivelaPriorityFacet(root, facetKey) {
  var drawer = getOlivelaPriorityFilterDrawer(root);
  if (!drawer) return;

  if (!drawer.hasAttribute("open")) {
    var trigger = root.querySelector(".psykhe__drawer-trigger");
    if (trigger instanceof HTMLElement) trigger.click();
  }

  openFacetInPriorityDrawer(drawer, facetKey);
}

function syncOlivelaPriorityFilters(root) {
  if (!(root instanceof HTMLElement)) {
    return;
  }

  var controls = root.querySelector(".psykhe__controls");
  if (!(controls instanceof HTMLElement)) {
    return;
  }

  var labels = getOlivelaPriorityFilterLabels();
  var existing = controls.querySelector(".psykhe-priority-filters");
  if (labels.length === 0 || window.innerWidth <= 1024) {
    if (existing instanceof HTMLElement) {
      existing.remove();
    }
    controls.classList.remove("psykhe-has-priority-filters");
    return;
  }

  var wrapper = existing;
  if (!(wrapper instanceof HTMLElement)) {
    wrapper = document.createElement("div");
    wrapper.className = "psykhe-priority-filters";
    controls.insertBefore(wrapper, controls.firstChild);
  }

  wrapper.innerHTML = "";
  var list = document.createElement("ul");
  list.className = "psykhe-priority-filters-list";

  labels.forEach(function (label) {
    var item = document.createElement("li");
    var button = document.createElement("button");
    var text = document.createElement("span");
    var icon = document.createElement("span");
    button.type = "button";
    button.className = "psykhe-priority-filter-button";
    text.className = "psykhe-priority-filter-label";
    text.textContent = label;
    icon.className = "psykhe-priority-filter-icon";
    icon.setAttribute("aria-hidden", "true");
    button.appendChild(text);
    button.appendChild(icon);
    button.setAttribute("data-facet-key", resolveOlivelaPriorityFacetKey(label));
    button.addEventListener("click", function () {
      openOlivelaPriorityFacet(root, button.getAttribute("data-facet-key") || "");
    });
    item.appendChild(button);
    list.appendChild(item);
  });

  wrapper.appendChild(list);
  controls.classList.add("psykhe-has-priority-filters");
}

// ---------------------------------------------------------------------------
// 9. SEARCH SUGGESTION DATA
//
// Fallback chips for <psykhe-search-input>. The dropdown row itself is
// rendered by renderOlivelaProductCard (shared with the grid) — the hookup
// in section 11 wraps it in an <li role="option"> for listbox a11y.
// ---------------------------------------------------------------------------

var OLIVELA_TRENDING_CHIPS = [
  { value: "dresses",    label: "Dresses",    url: "/collections/dresses" },
  { value: "swimsuit",   label: "Swimsuit",   url: "/collections/swim"    },
  { value: "mestiza",    label: "Mestiza",    url: "/search?mestiza" },
  { value: "gown",       label: "Gown",       url: "/collections/dresses?pf.category_2=Clothing+>+Dresses+>+Gown"  },
  { value: "rings",      label: "Rings",      url: "/collections/rings"  },
  { value: "skirts",     label: "Skirts",     url: "/collections/skirts" },
  { value: "aquamarine", label: "Aquamarine", url: "/search?q=aquamarine"},
];

// ---------------------------------------------------------------------------
// 10. BOOTSTRAP
//
// Runs on DOMContentLoaded plus typed PSYKHE lifecycle events.
// All init functions are idempotent via dataset flags.
// ---------------------------------------------------------------------------

var OLIVELA_PLP_ROOT_SELECTOR = ".psykhe, .psykhe--search";
var OLIVELA_PLP_DRAWER_ROOT_SELECTOR =
  '.psykhe[data-filter-position="drawer"], .psykhe--search[data-filter-position="drawer"]';

function initPsykheOlivelaPlp() {
  var plpRoots = document.querySelectorAll(OLIVELA_PLP_ROOT_SELECTOR);
  var drawerRoots = document.querySelectorAll(OLIVELA_PLP_DRAWER_ROOT_SELECTOR);

  runOlivelaInitStep("runtime-bindings", () => {
    initOlivelaRuntimeBindings();
  });

  runOlivelaInitStep("app-block", () => {
    plpRoots.forEach((root) => initOlivelaAppBlock(root));
  });

  runOlivelaInitStep("controls-bar", () => {
    plpRoots.forEach((root) => initOlivelaControlsBar(root));
  });

  runOlivelaInitStep("sticky-controls", () => {
    drawerRoots.forEach((root) => initStickyControls(root));
  });

  runOlivelaInitStep("drawer", () => {
    document
      .querySelectorAll("psykhe-drawer.psykhe__filter-drawer")
      .forEach((drawer) => initOlivelaDrawer(drawer));
  });

  runOlivelaInitStep("public-overrides", () => {
    applyOlivelaPublicOverrides(document);
  });

  runOlivelaInitStep("category-bootstrap", function () {
    if (olivelaAutoOpenState.didBootstrapCategoryOpen) return;

    var segments = getOlivelaCollectionSegments();
    if (segments.length === 0) return;

    // Requires real facet data from the store. handleOlivelaRefinementsChange
    // populates olivelaRuntimeState.facets synchronously on the first
    // non-suppressed psykhe-refinements-change event. This bootstrap runs from
    // a rAF-deferred initPsykheOlivelaPlp, so the handler always fires first.
    if (!Array.isArray(olivelaRuntimeState.facets)
      || olivelaRuntimeState.facets.length === 0) {
      return;
    }

    olivelaAutoOpenState.didBootstrapCategoryOpen = true;

    var keyToOpen = segments.length >= 2 ? "category_2" : "category_1";

    drawerRoots.forEach(function (root) {
      var drawer = getOlivelaPriorityFilterDrawer(root);
      openFacetInPriorityDrawer(drawer, keyToOpen);
    });

    // Seed prev-state from collection segments (mapped parent context) and
    // actual facet selections (cat2 may have URL-preselected values). Prevents
    // the first psykhe-refinements-change from re-detecting gain/loss.
    var bootFacets = olivelaRuntimeState.facets;
    if (segments.length >= 1) {
      olivelaAutoOpenState.prevCat0Selected = [segments[0]];
    }
    if (segments.length >= 2) {
      olivelaAutoOpenState.prevCat1Selected = [segments.slice(0, 2).join(" > ")];
    }
    olivelaAutoOpenState.prevCat2Selected =
      getSelectedFacetLabels(bootFacets, "category_2");
  });

  runOlivelaInitStep("content-tiles", () => {
    syncOlivelaContentTiles(document);
  });

  runOlivelaInitStep("pagination", () => {
    syncOlivelaPaginationVisibility(document);
  });
}

let olivelaPlpInitRafId = 0;
let psykheOlivelaSearchInputObserver = null;
function schedulePsykheOlivelaPlpInit() {
  if (olivelaPlpInitRafId) return;
  olivelaPlpInitRafId = window.requestAnimationFrame(() => {
    olivelaPlpInitRafId = 0;
    initPsykheOlivelaPlp();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPsykheOlivelaPlp, {
    once: true,
  });
} else {
  initPsykheOlivelaPlp();
}

document.addEventListener("psykhe-instance-ready", schedulePsykheOlivelaPlpInit);
document.addEventListener("psykhe-results-updated", schedulePsykheOlivelaPlpInit);
document.addEventListener(
  "psykhe-refinements-change",
  schedulePsykheOlivelaPlpInit
);

// The theme mounts <psykhe-search-input> in its <dialog> on demand; no
// document listener above fires on that insertion, so a MutationObserver
// runs applyOlivelaPublicOverrides at attach time, before the first render.
customElements.whenDefined('psykhe-search-input').then(function () {
  applyOlivelaPublicOverrides(document);

  // Idempotency guard prevents observer leak if bootstrap runs twice (theme editor preview).
  if (psykheOlivelaSearchInputObserver) return;

  psykheOlivelaSearchInputObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (!(node instanceof Element)) return;
        if (node.matches("psykhe-search-input")) {
          applyOlivelaPublicOverrides(node.parentElement || document);
        } else if (node.querySelector) {
          var found = node.querySelectorAll("psykhe-search-input");
          found.forEach(function (el) {
            applyOlivelaPublicOverrides(el.parentElement || document);
          });
        }
      });
    });
  });
  psykheOlivelaSearchInputObserver.observe(document, { childList: true, subtree: true });
});

// ---------------------------------------------------------------------------
// 11. PAGINATION SKELETON OVERLAY
//
// Shows a shimmer overlay on the product grid when a pagination request takes
// longer than `stalledDelayMs` to complete.
//
//   el.showPaginationSkeleton = true;   // disable with false
//   el.stalledDelayMs = 300;            // wait before showing the overlay
//   el.paginationMinDisplayMs = 300;    // minimum visible duration once shown
//
// Both delays default to 300 ms in the SDK (research-backed anti-flicker
// baseline). Raise `stalledDelayMs` (e.g. 400) to hide brief network hiccups,
// or raise `paginationMinDisplayMs` (e.g. 500) for a steadier overlay on slow
// connections. Lower either (e.g. 200) to react sooner and hide quicker.
// ---------------------------------------------------------------------------

customElements.whenDefined("psykhe-provider").then(function () {
  document.querySelectorAll("psykhe-provider").forEach(function (el) {
    el.showPaginationSkeleton = true;
    el.stalledDelayMs = 300;
    el.paginationMinDisplayMs = 300;
  });
});
